/* AiInvoiceImportModal — Upload a supplier invoice (PDF or photo).
 *
 * AI extracts header + line items and fuzzy-matches each line against
 * the product catalogue. User reviews the colour-coded list, picks
 * products for "Needs review" lines, optionally creates draft products
 * for "No match" lines, then `onApply` hands back the prepared rows
 * (including supplier_id, invoice_number, total, and matched line
 * items with new costs) so the parent GRV page can push them straight
 * into its cart.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { QRCodeCanvas } from "qrcode.react";
import { FileUp, Loader2, Sparkles, CheckCircle2, AlertTriangle, XCircle, Search, Plus, Smartphone, Copy, Mail, Trash2, RefreshCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "R " + Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AiInvoiceImportModal({ open, onClose, onApply, useInvoiceCost = true }) {
  const [stage, setStage] = useState("upload");   // upload | parsing | review
  const [parsed, setParsed] = useState(null);
  const [lineDecisions, setLineDecisions] = useState({});  // {idx: {chosen_product_id, use_cost: 'invoice'|'current'}}
  const [productSearchOpen, setProductSearchOpen] = useState(null);   // idx

  // Page tray — accumulates pasted/picked/dropped images for multi-page
  // invoices. A single PDF skips the tray and uploads immediately.
  const [pages, setPages] = useState([]);   // [{file, previewUrl}]

  // QR handoff state — open a session, show QR, poll phone uploads
  const [handoff, setHandoff] = useState(null);   // {token, mobile_url, expires_at}
  const [handoffStarting, setHandoffStarting] = useState(false);
  const handoffReceivedCountRef = useRef(0);

  // Email inbox state
  const [inbox, setInbox] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxLoadingId, setInboxLoadingId] = useState(null);
  const [loadedFromInboxId, setLoadedFromInboxId] = useState(null);

  const refreshInbox = async () => {
    setInboxLoading(true);
    try {
      const r = await axios.get(`${API}/api/grv/email-inbox/list`);
      setInbox(r.data?.rows || []);
    } catch (_e) { /* ignore */ }
    finally { setInboxLoading(false); }
  };

  useEffect(() => {
    if (open && stage === "upload") refreshInbox();
  }, [open, stage]);

  const loadFromInbox = async (item) => {
    setInboxLoadingId(item.id);
    try {
      const r = await axios.get(`${API}/api/grv/email-inbox/${item.id}/preview`);
      const file = await dataUrlToFile(r.data.data_url, r.data.filename || item.filename);
      if (file.type === "application/pdf") {
        setLoadedFromInboxId(item.id);
        await submitFiles([file]);
      } else {
        setPages((cur) => [...cur, { file, previewUrl: URL.createObjectURL(file), fromInboxId: item.id }]);
        setLoadedFromInboxId(item.id);
        toast.success("Email attachment added to page tray");
      }
    } catch (e) {
      toast.error("Could not load email attachment");
    } finally {
      setInboxLoadingId(null);
    }
  };

  const dismissInbox = async (item) => {
    try {
      await axios.post(`${API}/api/grv/email-inbox/${item.id}/dismiss`);
      setInbox((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Email dismissed");
    } catch (_e) {
      toast.error("Could not dismiss");
    }
  };

  useEffect(() => {
    if (!open) {
      setStage("upload"); setParsed(null); setLineDecisions({}); setProductSearchOpen(null);
      // Free any object URLs
      pages.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPages([]);
      // Close any active phone handoff
      if (handoff?.token) {
        axios.post(`${API}/api/grv/handoff/${handoff.token}/close`).catch(() => {});
      }
      setHandoff(null);
      handoffReceivedCountRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitFiles = async (filesToSend) => {
    if (!filesToSend || filesToSend.length === 0) return;
    setStage("parsing");
    try {
      const form = new FormData();
      filesToSend.forEach((f) => form.append("files", f));
      // Kick off an ASYNC job so the upload itself returns in <2s — this
      // sidesteps Cloudflare's 100-second edge timeout for multi-page PDFs
      // which can take 30–90s on Gemini.
      const start = await axios.post(`${API}/api/grv/parse-invoice-async`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Could not start parse job");

      // Poll every 2.5s for up to 5 minutes. On each tick we get a fresh
      // request through the Cloudflare edge, so no single request can hit
      // the 100-second timeout.
      const maxAttempts = 120;   // 120 × 2.5s = 5 min
      let attempt = 0;
      while (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 2500));
        attempt += 1;
        const poll = await axios.get(`${API}/api/grv/parse-invoice-async/${jobId}`, { timeout: 15000 });
        const status = poll.data?.status;
        if (status === "done") {
          const result = poll.data.result;
          setParsed(result);
          const decisions = {};
          (result.lines || []).forEach((ln, idx) => {
            decisions[idx] = {
              chosen_product_id: ln.matched_product?.id || null,
              use_cost: useInvoiceCost ? "invoice" : "current",
              skip: false,
            };
          });
          setLineDecisions(decisions);
          setStage("review");
          return;
        }
        if (status === "error") {
          throw new Error(poll.data?.error || "AI parse failed");
        }
        // still running — keep polling
      }
      throw new Error("AI parse timed out after 5 minutes — please retry");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI parse failed");
      setStage("upload");
    }
  };

  const addFile = (file) => {
    if (!file) return;
    // PDFs are processed alone (Gemini handles their pagination natively)
    if (file.type === "application/pdf") {
      submitFiles([file]);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Only images or PDFs are accepted");
      return;
    }
    setPages((cur) => [...cur, { file, previewUrl: URL.createObjectURL(file) }]);
    toast.success(`Page ${pages.length + 1} added`);
  };

  const removePage = (idx) => {
    setPages((cur) => {
      const copy = [...cur];
      const removed = copy.splice(idx, 1)[0];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return copy;
    });
  };

  const processAccumulated = () => {
    if (pages.length === 0) return;
    submitFiles(pages.map(p => p.file));
  };

  const handleFile = (file) => addFile(file);

  // Ctrl+V / Cmd+V paste support — accepts an image from the clipboard
  // (screenshot, copied photo, etc.) and appends it to the page tray.
  // PDF on clipboard uploads immediately (Gemini handles its pages natively).
  useEffect(() => {
    if (!open || stage !== "upload") return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.kind === "file") {
          const file = it.getAsFile();
          if (file && (file.type.startsWith("image/") || file.type === "application/pdf")) {
            e.preventDefault();
            addFile(file);
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage, pages.length]);

  const onDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer?.files || []);
    list.forEach(addFile);
  };

  // ----- Phone handoff (QR) -----
  const startPhoneHandoff = async () => {
    if (handoff || handoffStarting) return;
    setHandoffStarting(true);
    try {
      const res = await axios.post(`${API}/api/grv/handoff/create`);
      setHandoff(res.data);
      handoffReceivedCountRef.current = 0;
      toast.success("Phone link ready — scan the QR with your phone camera");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start phone handoff");
    } finally {
      setHandoffStarting(false);
    }
  };

  const stopPhoneHandoff = async () => {
    if (!handoff?.token) return;
    try { await axios.post(`${API}/api/grv/handoff/${handoff.token}/close`); } catch (_e) { /* ignore */ }
    setHandoff(null);
    handoffReceivedCountRef.current = 0;
  };

  // Convert data URL → File so it can join the regular page tray
  const dataUrlToFile = async (dataUrl, filename) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename || "phone-page.jpg", { type: blob.type });
  };

  // Poll the handoff session every 2 s while the QR is showing
  useEffect(() => {
    if (!open || stage !== "upload" || !handoff?.token) return;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const r = await axios.get(
          `${API}/api/grv/handoff/${handoff.token}/poll`,
          { params: { since: handoffReceivedCountRef.current } }
        );
        const news = r.data?.new_images || [];
        // Bump the ref IMMEDIATELY so any subsequent tick that fires
        // during the image-conversion await loop below won't re-fetch
        // (and thus duplicate) these same images.
        if (typeof r.data?.image_count === "number") {
          handoffReceivedCountRef.current = r.data.image_count;
        }
        if (news.length > 0) {
          for (const img of news) {
            const f = await dataUrlToFile(img.data_url, img.filename);
            if (cancelled) break;
            setPages((cur) => [...cur, { file: f, previewUrl: URL.createObjectURL(f), fromPhone: true }]);
          }
          toast.success(`${news.length} page${news.length === 1 ? "" : "s"} arrived from phone`);
        }
      } catch (_e) {
        // Stop polling on hard error (e.g., session expired)
      } finally {
        inFlight = false;
      }
    };
    const id = setInterval(tick, 2000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage, handoff?.token]);

  const copyMobileUrl = () => {
    if (!handoff?.mobile_url) return;
    const full = `${window.location.origin}${handoff.mobile_url}`;
    navigator.clipboard?.writeText(full);
    toast.success("Link copied — paste into your phone browser");
  };

  const mobileFullUrl = handoff ? `${window.location.origin}${handoff.mobile_url}` : "";

  const applyToGRV = () => {
    if (!parsed) return;
    const rows = [];
    parsed.lines.forEach((ln, idx) => {
      const d = lineDecisions[idx] || {};
      if (d.skip) return;
      if (!d.chosen_product_id) return;   // unresolved
      const product = ln.matched_product?.id === d.chosen_product_id
        ? ln.matched_product
        : (ln.suggestions || []).find(s => s.id === d.chosen_product_id) || null;
      rows.push({
        product_id: d.chosen_product_id,
        product_name: product?.name || ln.description,
        plu_code: product?.plu_code,
        current_stock: product?.current_stock || 0,
        previous_unit_cost: product?.cost_price || 0,
        quantity_to_receive: Number(ln.quantity) || 0,
        new_unit_cost: d.use_cost === "current"
          ? Number(product?.cost_price || 0)
          : Number(ln.unit_cost_excl_vat) || 0,
        ai_imported: true,
        ai_invoice_description: ln.description,
      });
    });
    if (rows.length === 0) {
      toast.error("No lines selected. Pick a product for at least one line or skip and add manually.");
      return;
    }
    onApply({
      supplier_match: parsed.matched_supplier,
      supplier_name: parsed.supplier_name,
      invoice_number: parsed.invoice_number,
      invoice_date: parsed.invoice_date,
      invoice_total_incl_vat: parsed.total_incl_vat,
      invoice_subtotal_excl_vat: parsed.subtotal_excl_vat,
      rows,
    });
    // If this batch was loaded from the email inbox, clear it from there
    if (loadedFromInboxId) {
      axios.post(`${API}/api/grv/email-inbox/${loadedFromInboxId}/mark-imported`).catch(() => {});
    }
    onClose && onClose();
  };

  const createDraftProduct = async (idx, line) => {
    try {
      const res = await axios.post(`${API}/api/grv/parse-invoice/create-draft-product`, {
        name: line.description,
        cost_price: line.unit_cost_excl_vat,
        supplier_id: parsed?.matched_supplier?.id || null,
        supplier_code: line.supplier_code || null,
        description_from_invoice: line.description,
      });
      toast.success(`Draft product created — please review & set PLU later in Stock Master`);
      // Mutate locally to reflect the new match
      setParsed((p) => {
        const copy = { ...p, lines: p.lines.map((l, i) => i === idx ? {
          ...l, matched_product: res.data, matched_by: "draft_created", confidence: 1.0
        } : l) };
        return copy;
      });
      setLineDecisions((d) => ({ ...d, [idx]: { ...(d[idx] || {}), chosen_product_id: res.data.id } }));
    } catch (e) {
      toast.error("Could not create draft product");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            AI Invoice Import
          </DialogTitle>
        </DialogHeader>

        {/* STAGE 1 — upload */}
        {stage === "upload" && (
          <div className="py-6">
            <label
              className="block border-2 border-dashed border-purple-300 rounded-lg p-8 cursor-pointer hover:bg-purple-50 transition text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
            >
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
                multiple
                className="hidden"
                onChange={(e) => Array.from(e.target.files || []).forEach(addFile)}
                data-testid="invoice-file-input"
              />
              <FileUp className="h-10 w-10 mx-auto text-purple-500 mb-2" />
              <div className="text-base font-semibold">Drop, pick, or paste your invoice</div>
              <div className="text-xs text-gray-500 mt-1">
                One PDF, or one+ images (one per page). Max 25 MB each.
              </div>
              <div className="text-xs text-purple-600 mt-3 flex items-center justify-center gap-3">
                <span>📋 <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] font-mono">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] font-mono">V</kbd> to paste — paste each page in sequence for multi-page</span>
              </div>
            </label>

            {/* Page tray */}
            {pages.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-purple-800">
                    {pages.length} page{pages.length === 1 ? "" : "s"} ready
                  </div>
                  <Button
                    size="sm"
                    onClick={processAccumulated}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="process-pages-btn"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Process {pages.length} page{pages.length === 1 ? "" : "s"}
                  </Button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {pages.map((p, idx) => (
                    <div key={idx} className="relative border rounded overflow-hidden bg-white">
                      <img
                        src={p.previewUrl}
                        alt={`page ${idx + 1}`}
                        className="w-full h-24 object-cover"
                      />
                      <div className="absolute top-0 left-0 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5">
                        Pg {idx + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePage(idx)}
                        className="absolute top-0 right-0 bg-red-600 text-white p-0.5 hover:bg-red-700"
                        title="Remove page"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-2 italic">
                  Tip: paste another image to add the next page. Pages are sent in order.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4 text-center">
              Powered by Gemini 3 Pro. ~10–20 seconds per invoice.
            </p>

            {/* Email Inbox — invoices forwarded from suppliers */}
            {(inbox.length > 0 || inboxLoading) && (
              <div className="mt-5 border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                    <Mail className="h-4 w-4 text-blue-600" />
                    Email inbox
                    {inbox.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-300">{inbox.length}</Badge>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={refreshInbox} disabled={inboxLoading} className="h-7 text-xs">
                    <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${inboxLoading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {inbox.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 border rounded p-2 bg-blue-50/40 hover:bg-blue-50">
                      <Mail className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" title={it.subject}>{it.subject || "(no subject)"}</div>
                        <div className="text-[10px] text-gray-500 truncate" title={it.from_address}>
                          {it.from_address || "(no sender)"} · {it.filename} · {(it.size_bytes / 1024).toFixed(0)} KB
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => loadFromInbox(it)}
                        disabled={inboxLoadingId === it.id}
                        className="bg-blue-600 hover:bg-blue-700 h-7 px-2 text-[11px] flex-shrink-0"
                        data-testid={`load-inbox-${it.id}`}
                      >
                        {inboxLoadingId === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Load"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => dismissInbox(it)}
                        className="h-7 px-2 text-[11px] flex-shrink-0 text-gray-500 hover:text-red-700"
                        title="Dismiss"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phone handoff (QR) */}
            <div className="mt-5 border-t pt-4">
              {!handoff ? (
                <div className="flex items-center justify-between gap-3 bg-purple-50 border border-purple-200 rounded p-3">
                  <div className="flex items-start gap-3">
                    <Smartphone className="h-5 w-5 text-purple-700 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-purple-900">Use your phone camera</div>
                      <div className="text-xs text-purple-700/80">
                        Scan a QR code with your phone, snap each page, finish on this desktop.
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={startPhoneHandoff}
                    disabled={handoffStarting}
                    className="bg-purple-600 hover:bg-purple-700 flex-shrink-0"
                    data-testid="start-phone-handoff-btn"
                  >
                    {handoffStarting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>Show QR</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
                        <Smartphone className="h-4 w-4" /> Waiting for phone…
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Pages auto-arrive in the tray above as you snap them on your phone.
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={stopPhoneHandoff} className="text-xs h-7">
                      <XCircle className="h-3.5 w-3.5 mr-1" /> End session
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 border rounded shadow-sm">
                      <QRCodeCanvas value={mobileFullUrl} size={148} includeMargin={false} />
                    </div>
                    <div className="text-xs flex-1 min-w-0">
                      <ol className="list-decimal pl-4 space-y-1 text-gray-700">
                        <li>Open your phone camera</li>
                        <li>Point it at the QR code</li>
                        <li>Tap the link that appears</li>
                        <li>Snap each invoice page</li>
                      </ol>
                      <div className="mt-2 flex gap-1.5">
                        <input
                          readOnly
                          value={mobileFullUrl}
                          className="flex-1 text-[10px] font-mono border rounded px-1.5 py-1 bg-gray-50 truncate"
                          onFocus={(e) => e.target.select()}
                        />
                        <Button size="sm" variant="outline" onClick={copyMobileUrl} className="h-7 px-2 text-xs">
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STAGE 2 — parsing */}
        {stage === "parsing" && (
          <div className="py-16 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-purple-500 mb-3" />
            <div className="font-semibold">Reading your invoice…</div>
            <div className="text-xs text-gray-500 mt-1">Extracting supplier, items, and matching to your stock master</div>
          </div>
        )}

        {/* STAGE 3 — review */}
        {stage === "review" && parsed && (
          <div className="space-y-3">
            {/* Header */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm bg-gray-50 border rounded p-3">
              <div>
                <div className="text-xs text-gray-500">Supplier</div>
                <div className="font-semibold">
                  {parsed.matched_supplier ? (
                    <span className="text-green-700">✓ {parsed.matched_supplier.name}</span>
                  ) : (
                    <span className="text-amber-700">⚠ {parsed.supplier_name || "(not detected)"}</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Invoice #</div>
                <div className="font-mono">{parsed.invoice_number || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Date</div>
                <div>{parsed.invoice_date || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total (incl VAT)</div>
                <div className="font-bold">{fmt(parsed.total_incl_vat)}</div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-2 text-xs">
              <Badge className="bg-green-100 text-green-800 border-green-300">{parsed.stats.high_confidence} matched</Badge>
              <Badge className="bg-amber-100 text-amber-800 border-amber-300">{parsed.stats.needs_review} need review</Badge>
              <Badge className="bg-red-100 text-red-800 border-red-300">{parsed.stats.no_match} no match</Badge>
            </div>

            {/* Lines */}
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="p-2">Invoice line</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Invoice cost</th>
                    <th className="p-2">Matched product</th>
                    <th className="p-2 text-right">Current cost</th>
                    <th className="p-2">Use which cost?</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.lines.map((ln, idx) => {
                    const d = lineDecisions[idx] || {};
                    const matched = ln.matched_product;
                    const conf = ln.confidence || 0;
                    const status = matched
                      ? (conf >= 0.9 ? "ok" : "review")
                      : "none";
                    const rowCls = status === "ok"
                      ? "bg-green-50/50"
                      : status === "review"
                      ? "bg-amber-50/50"
                      : "bg-red-50/50";
                    return (
                      <tr key={idx} className={`border-t ${rowCls} ${d.skip ? "opacity-40" : ""}`}>
                        <td className="p-2 max-w-[230px]">
                          <div className="font-medium truncate" title={ln.description}>{ln.description}</div>
                          <div className="text-[10px] text-gray-500">
                            {ln.supplier_code && <span>code: {ln.supplier_code}</span>}
                          </div>
                        </td>
                        <td className="p-2 text-right">{ln.quantity}</td>
                        <td className="p-2 text-right">{fmt(ln.unit_cost_excl_vat)}</td>
                        <td className="p-2 max-w-[200px]">
                          {matched ? (
                            <div>
                              <div className="font-medium truncate flex items-center gap-1" title={matched.name}>
                                {status === "ok" ? <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" /> : <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" />}
                                {matched.name}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                PLU {matched.plu_code || "—"} · conf {(conf * 100).toFixed(0)}%
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-600">
                              <XCircle className="h-3 w-3" />
                              No match
                            </div>
                          )}
                          {ln.suggestions?.length > 0 && (
                            <select
                              className="w-full mt-1 border rounded text-[11px] p-1"
                              value={d.chosen_product_id || ""}
                              onChange={(e) => setLineDecisions((cur) => ({ ...cur, [idx]: { ...(cur[idx] || {}), chosen_product_id: e.target.value || null } }))}
                            >
                              <option value="">— pick a suggestion —</option>
                              {ln.suggestions.map((s, si) => (
                                <option key={si} value={s.id}>{s.name} ({(s.score * 100).toFixed(0)}%)</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="p-2 text-right">{matched ? fmt(matched.cost_price) : "—"}</td>
                        <td className="p-2">
                          <div className="flex gap-1 text-[11px]">
                            <Button size="sm" variant={d.use_cost === "invoice" ? "default" : "outline"}
                                    onClick={() => setLineDecisions(c => ({ ...c, [idx]: { ...(c[idx] || {}), use_cost: "invoice" } }))}
                                    className={`h-7 px-2 text-[11px] ${d.use_cost === "invoice" ? "bg-purple-600 hover:bg-purple-700" : ""}`}>
                              Invoice
                            </Button>
                            <Button size="sm" variant={d.use_cost === "current" ? "default" : "outline"}
                                    onClick={() => setLineDecisions(c => ({ ...c, [idx]: { ...(c[idx] || {}), use_cost: "current" } }))}
                                    className={`h-7 px-2 text-[11px] ${d.use_cost === "current" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                                    disabled={!matched}>
                              Current
                            </Button>
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          {!matched && (
                            <Button size="sm" variant="ghost" onClick={() => createDraftProduct(idx, ln)} title="Create as draft product"
                                    className="h-7 px-2 text-[11px] text-purple-700">
                              <Plus className="h-3 w-3 mr-1" /> Create draft
                            </Button>
                          )}
                          <Button size="sm" variant="ghost"
                                  onClick={() => setLineDecisions(c => ({ ...c, [idx]: { ...(c[idx] || {}), skip: !c[idx]?.skip } }))}
                                  className="h-7 px-2 text-[11px]">
                            {d.skip ? "Include" : "Skip"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500">
              Tip: lines without a matched product won't be added to the GRV until you pick or create one. "Skip" excludes the line entirely.
            </p>
          </div>
        )}

        <DialogFooter>
          {stage === "review" && (
            <>
              <Button variant="outline" onClick={() => setStage("upload")} disabled={!parsed}>← Re-upload</Button>
              <Button onClick={applyToGRV} className="bg-purple-600 hover:bg-purple-700" data-testid="apply-ai-import">
                Apply to GRV
              </Button>
            </>
          )}
          {stage === "upload" && (
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
